import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ForgottenPasswordModel } from '@app/models/auth/forgottenPassword.model';
import { ForgottenPasswordResponse } from '@app/models/auth/forgottenPasswordResponse.model';
import { LoginRequestModel } from '@app/models/auth/loginRequest.model';
import { LoginResponseModel } from '@app/models/auth/loginResponse.model';
import { PasswordResetModel } from '@app/models/auth/passwordReset.model';
import { PasswordResetResponse } from '@app/models/auth/passwordResetResponse.model';
import { RegisterRequestModel } from '@app/models/auth/registerRequest.model';
import { RegisterResponseModel } from '@app/models/auth/registerResponse.model';
import { ResendActivationModel } from '@app/models/auth/resendActivation.model';
import { ResendActivationResponse } from '@app/models/auth/resendActivationResponse.model';
import { UserDataResponse } from '@app/models/auth/userDataResponse.model';
import * as Sentry from '@sentry/browser';
import { Observable, Subscriber } from 'rxjs/Rx';

import { AppConfig } from './../app.config';
import { CourseService } from './course.service';
import { UserService } from './user.service';
import { ActivateUserResponseModel } from '@app/models/auth/activateUserResponse.model';
import { ForcePasswordResetModel } from '@app/models/auth/forcePasswordReset.model';
import { CodeResponseModel } from '@app/models/auth/codeResponse.model';

@Injectable()
export class AuthService {
  private signedOut: boolean = false;

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private courseService: CourseService
  ) {}

  private clearCache() {
    Sentry.configureScope(scope => {
      scope.setUser({});
    });
    this.userService.clearCache();
    this.courseService.clearCourseListCache();
    sessionStorage.clear();
    document.cookie = 'state=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }

  getSignedOut() {
    return this.signedOut;
  }

  setSignedOut(value: boolean) {
    this.signedOut = value;
  }

  isAuthenticated(): boolean {
    var token = sessionStorage.getItem('token');
    return !!token;
  }

  public getAuthorizationToken(): string {
    var token = sessionStorage.getItem('token');
    return token;
  }

  public checkCode(discountCode: string) {
    var url = AppConfig.ApiUrl + 'checkcode';

    return new Observable((sub: Subscriber<CodeResponseModel>) => {
      this.http.post<CodeResponseModel>(url, { uuid: discountCode }).subscribe(
        data => {
          if (data.success) {
            sub.next(data);
            return;
          }
          sub.error(data);
        },
        err => {
          sub.error(err.error as CodeResponseModel);
        },
        () => {
          sub.complete();
        }
      );
    });
  }

  login(model: LoginRequestModel) {
    var url = AppConfig.ApiUrl + 'login';

    return new Observable((sub: Subscriber<LoginResponseModel>) => {
      this.http.post<LoginResponseModel>(url, model).subscribe(
        data => {
          if (data.success) {
            this.clearCache();
            sessionStorage.setItem('token', data.token);
            sessionStorage.setItem('q-id', data.questionnaireId);
            Sentry.configureScope(scope => {
              scope.setUser({
                userId: data.userId
              });
            });
            sub.next(data);
            return;
          }
          sub.error(data);
        },
        err => {
          sub.error(err.error as LoginResponseModel);
        },
        () => {
          sub.complete();
        }
      );
    });
  }

  logout(showMessage: boolean = true): void {
    if (showMessage == true) {
      this.setSignedOut(true); // To display logout message
    }
    this.clearCache();
  }

  register(model: RegisterRequestModel): Observable<RegisterResponseModel> {
    var url = AppConfig.ApiUrl + 'user/register';

    return new Observable((sub: Subscriber<RegisterResponseModel>) => {
      this.http.post<RegisterResponseModel>(url, model).subscribe(
        data => {
          if (data.success) {
            this.clearCache();
            sessionStorage.setItem('token', data.token);
            sessionStorage.setItem('q-id', data.questionnaireId);
            Sentry.configureScope(scope => {
              scope.setUser({
                userId: data.userId
              });
            });
            sub.next(data);
            return;
          }
          sub.error(data);
        },
        err => {
          sub.error(err.error as RegisterResponseModel);
        },
        () => {
          sub.complete();
        }
      );
    });
  }

  activateUser(token: string): Observable<ActivateUserResponseModel> {
    var url = AppConfig.ApiUrl + `user/activation/${token}`;
    return this.http.post<ActivateUserResponseModel>(url, null);
  }

  forceResetPassword(model: ForcePasswordResetModel): Observable<PasswordResetResponse> {
    var url = AppConfig.ApiUrl + 'user';
    return this.http
      .put<PasswordResetResponse>(url, model)
      .catch(err => Observable.throw(err.error as PasswordResetResponse));
  }

  forgottenPassword(model: ForgottenPasswordModel): Observable<ForgottenPasswordResponse> {
    var url = AppConfig.ApiUrl + 'user/password/email';
    return this.http
      .post<ForgottenPasswordResponse>(url, model)
      .catch(err => Observable.throw(err.error as ForgottenPasswordResponse));
  }

  resetPassword(model: PasswordResetModel): Observable<PasswordResetResponse> {
    var url = AppConfig.ApiUrl + 'user/password/reset';
    return this.http
      .post<PasswordResetResponse>(url, model)
      .catch(err => Observable.throw(err.error as PasswordResetResponse));
  }

  resendActivation(model: ResendActivationModel): Observable<ResendActivationResponse> {
    var url =
      AppConfig.ApiUrl +
      'user/activation' +
      '?email=' +
      model.email +
      '&url=' +
      encodeURIComponent(model.url);

    return this.http
      .get<ResendActivationResponse>(url)
      .catch(err => Observable.throw(err.error as ResendActivationResponse));
  }

  getGdprData(model: any): Observable<UserDataResponse> {
    var url = AppConfig.ApiUrl + 'user/data';
    return this.http
      .post<UserDataResponse>(url, model)
      .catch(err => Observable.throw(err.error as UserDataResponse));
  }

  deleteGdprData(model: any): Observable<UserDataResponse> {
    var url = AppConfig.ApiUrl + 'user';
    return this.http
      .delete<UserDataResponse>(url, {
        params: new HttpParams().set('password', model.password)
      })
      .catch(err => Observable.throw(err.error as UserDataResponse));
  }

  loginWithToken(token: string) {
    var url = AppConfig.ApiUrl + 'login-with-token';
    return new Observable((sub: Subscriber<LoginResponseModel>) => {
      this.http.post<LoginResponseModel>(url, { token: token }).subscribe(
        data => {
          if (data.success) {
            this.clearCache();
            sessionStorage.setItem('token', data.token);
            sessionStorage.setItem('q-id', data.questionnaireId);
            Sentry.configureScope(scope => {
              scope.setUser({
                userId: data.userId
              });
            });
            sub.next(data);
            return;
          }
          sub.error(data);
        },
        err => {
          sub.error(err.error as LoginResponseModel);
        },
        () => {
          sub.complete();
        }
      );
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/auth.service.ts